import { CircularProgress } from '@mui/material'
import { pantryColors } from '@pantry/ui'
import type React from 'react'

// Shared chrome for the ClickHouse-backed analytics cards (Sessions / Device
// split / Search terms / Top earners / Geo map). Each card is a bordered
// sub-panel inside the Analytics surface with a title row and a common
// loading / error / empty treatment matching the module's existing patterns.

const p = pantryColors

export const panelStyle: React.CSSProperties = {
  border: `1px solid ${p.gray[100]}`,
  borderRadius: 8,
  background: p.white,
  padding: 16,
  minWidth: 0,
}

export const panelTitleStyle: React.CSSProperties = {
  fontSize: 12.5,
  fontWeight: 600,
  color: p.gray[700],
}

export const panelSubtitleStyle: React.CSSProperties = {
  fontSize: 11.5,
  color: p.gray[400],
  marginTop: 2,
}

export const PanelLoading: React.FC = () => (
  <div style={{ padding: '28px 0', textAlign: 'center', color: p.gray[500] }}>
    <CircularProgress size={18} thickness={5} sx={{ color: p.brand.primary }} />
  </div>
)

export const PanelError: React.FC<{ message?: string }> = ({ message }) => (
  <div
    style={{
      padding: '24px 0',
      textAlign: 'center',
      fontSize: 13,
      color: p.gray[500],
    }}
  >
    {message ?? "Couldn't load this card. Please try again."}
  </div>
)

// Same empty-state block the Analytics tables/charts use.
export const PanelEmpty: React.FC<{ text: string }> = ({ text }) => (
  <div
    style={{
      padding: '20px 0',
      textAlign: 'center',
      fontSize: 13,
      color: p.gray[400],
      background: p.gray[50],
      borderRadius: 6,
    }}
  >
    {text}
  </div>
)

export const formatCount = (value: number): string =>
  new Intl.NumberFormat('en-US').format(Number.isFinite(value) ? value : 0)

export const formatCurrency = (value: number): string =>
  `$${new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(value) ? value : 0)}`

// "3m 20s" style duration (falls back to "45s" under a minute, "1h 2m" over
// an hour) — used for avg session durations.
export const formatDuration = (seconds: number): string => {
  const total = Math.max(0, Math.round(Number.isFinite(seconds) ? seconds : 0))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  if (h > 0) return `${h}h ${m}m`
  if (m > 0) return `${m}m ${s}s`
  return `${s}s`
}
